import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api-client";

type SurveyQuestionType = "single_choice" | "multiple_choice" | "rating" | "open_text";
type SurveyDisplayAt = "pre" | "during" | "post";
type SurveyStatus = "draft" | "active" | "closed" | "archived";
type SurveyType = "quick_poll" | "nps" | "multi_question" | "rating_matrix" | "open_ended" | "prioritization";

interface SurveyOption {
  id: string;
  label: string;
}

interface SurveyQuestion {
  id: string;
  type: SurveyQuestionType;
  prompt: string;
  required?: boolean;
  options?: SurveyOption[];
}

interface WebinarSurvey {
  id: string;
  title: string;
  surveyType: SurveyType;
  displayAt: SurveyDisplayAt;
  questions: SurveyQuestion[];
  status: SurveyStatus;
  sortOrder: number;
  launchedAt: string | null;
  closedAt: string | null;
}

interface SurveyResultsQuestion {
  questionId: string;
  prompt: string;
  type: SurveyQuestionType;
  counts?: Array<{ optionId: string; label: string; responses: number }>;
  average?: number | null;
  responses?: number | string[];
}

interface SurveyResults {
  surveyId: string;
  totalResponses: number;
  questions: SurveyResultsQuestion[];
}

interface WebinarSurveyPanelProps {
  webinarId?: string;
  displayAt: SurveyDisplayAt;
  theme?: "light" | "dark";
  className?: string;
  title?: string;
  emptyLabel?: string;
}

type AnswerValue = string | number | boolean | string[] | null;

const panelTheme = {
  light: {
    panel: "rounded-xl border border-border bg-card",
    muted: "text-muted-foreground",
    title: "text-foreground",
    field: "border border-input bg-background text-foreground",
    button: "bg-primary text-primary-foreground hover:bg-primary/90",
    secondaryButton: "border border-border bg-background text-foreground hover:bg-accent",
    badge: "bg-primary/10 text-primary",
  },
  dark: {
    panel: "rounded-xl border border-gray-800 bg-gray-900/95",
    muted: "text-gray-400",
    title: "text-white",
    field: "border border-gray-700 bg-gray-800 text-white",
    button: "bg-primary text-white hover:bg-primary/90",
    secondaryButton: "border border-gray-700 bg-gray-800 text-white hover:bg-gray-700",
    badge: "bg-primary/20 text-primary-foreground",
  },
} as const;

function toClassName(...parts: Array<string | undefined | false>) {
  return parts.filter(Boolean).join(" ");
}

function getRatingRange(surveyType: SurveyType) {
  if (surveyType === "nps") {
    return Array.from({ length: 11 }, (_, index) => index);
  }

  return Array.from({ length: 5 }, (_, index) => index + 1);
}

export function WebinarSurveyPanel({
  webinarId,
  displayAt,
  theme = "light",
  className,
  title,
  emptyLabel,
}: WebinarSurveyPanelProps) {
  const styles = panelTheme[theme];
  const [surveys, setSurveys] = useState<WebinarSurvey[]>([]);
  const [answersBySurvey, setAnswersBySurvey] = useState<Record<string, Record<string, AnswerValue>>>({});
  const [submittedSurveyIds, setSubmittedSurveyIds] = useState<Record<string, boolean>>({});
  const [resultsBySurvey, setResultsBySurvey] = useState<Record<string, SurveyResults | undefined>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!webinarId) {
      setSurveys([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function loadSurveys() {
      try {
        setIsLoading(true);
        setError(null);
        const response = await apiFetch<{ data: WebinarSurvey[] }>(`/v1/webinars/${webinarId}/surveys`);
        if (cancelled) return;
        setSurveys(response.data ?? []);
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Could not load webinar surveys.");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadSurveys();

    return () => {
      cancelled = true;
    };
  }, [webinarId]);

  const activeSurveys = useMemo(
    () => surveys.filter((survey) => survey.displayAt === displayAt && survey.status === "active"),
    [displayAt, surveys],
  );

  const activeSurvey = activeSurveys[0] ?? null;
  const answerDraft = activeSurvey ? (answersBySurvey[activeSurvey.id] ?? {}) : {};

  async function loadResults(surveyId: string) {
    if (!webinarId) return;

    const response = await apiFetch<{ data: SurveyResults }>(`/v1/webinars/${webinarId}/surveys/${surveyId}/results`);
    setResultsBySurvey((current) => ({ ...current, [surveyId]: response.data }));
  }

  function updateAnswer(questionId: string, value: AnswerValue) {
    if (!activeSurvey) return;

    setAnswersBySurvey((current) => ({
      ...current,
      [activeSurvey.id]: {
        ...(current[activeSurvey.id] ?? {}),
        [questionId]: value,
      },
    }));
  }

  async function handleSubmit() {
    if (!webinarId || !activeSurvey) return;

    for (const question of activeSurvey.questions) {
      const answer = answerDraft[question.id];
      const missingArray = Array.isArray(answer) && answer.length === 0;
      const missingScalar = answer === undefined || answer === null || answer === "";
      if (question.required && (missingScalar || missingArray)) {
        setError(`Complete the required question: ${question.prompt}`);
        return;
      }
    }

    try {
      setError(null);
      setIsSubmitting(activeSurvey.id);
      await apiFetch<{ data: { id: string } }>(`/v1/webinars/${webinarId}/surveys/${activeSurvey.id}/respond`, {
        method: "POST",
        body: JSON.stringify({ answers: answerDraft }),
      });
      setSubmittedSurveyIds((current) => ({ ...current, [activeSurvey.id]: true }));
      await loadResults(activeSurvey.id);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not submit webinar survey.");
    } finally {
      setIsSubmitting(null);
    }
  }

  if (!webinarId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={toClassName(styles.panel, className, "p-4")}>
        <div className="space-y-3 animate-pulse">
          <div className="h-4 w-32 rounded bg-current/10" />
          <div className="h-12 rounded bg-current/10" />
          <div className="h-12 rounded bg-current/10" />
        </div>
      </div>
    );
  }

  if (error && !activeSurvey) {
    return (
      <div className={toClassName(styles.panel, className, "p-4")}>
        <p className="text-sm text-[var(--color-destructive)]">{error}</p>
      </div>
    );
  }

  if (!activeSurvey) {
    return (
      <div className={toClassName(styles.panel, className, "p-4")}>
        <p className={toClassName("text-sm", styles.muted)}>
          {emptyLabel ?? "No active survey is scheduled for this stage."}
        </p>
      </div>
    );
  }

  const submitted = submittedSurveyIds[activeSurvey.id] === true;
  const results = resultsBySurvey[activeSurvey.id];

  return (
    <section className={toClassName(styles.panel, className, "space-y-4 p-4")}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={toClassName("text-xs uppercase tracking-wide", styles.muted)}>
            {title ?? `${displayAt} webinar survey`}
          </p>
          <h3 className={toClassName("mt-1 text-base font-semibold", styles.title)}>{activeSurvey.title}</h3>
        </div>
        <span className={toClassName("rounded-full px-2.5 py-1 text-xs font-medium", styles.badge)}>
          {activeSurvey.surveyType.replaceAll("_", " ")}
        </span>
      </div>

      {submitted ? (
        <div className="space-y-3">
          <div className={toClassName("rounded-lg border p-3 text-sm", styles.field)}>
            Thanks. Your response has been recorded.
          </div>
          {results ? (
            <div className="space-y-3">
              <p className={toClassName("text-xs uppercase tracking-wide", styles.muted)}>
                Aggregated results • {results.totalResponses} responses
              </p>
              {results.questions.map((question) => (
                <div key={question.questionId} className={toClassName("rounded-lg border p-3", styles.field)}>
                  <p className={toClassName("text-sm font-medium", styles.title)}>{question.prompt}</p>
                  {question.counts ? (
                    <div className="mt-2 space-y-2">
                      {question.counts.map((count) => (
                        <div key={count.optionId} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className={styles.muted}>{count.label}</span>
                            <span className={styles.title}>{count.responses}</span>
                          </div>
                          <div className="h-2 rounded-full bg-current/10">
                            <div
                              className="h-2 rounded-full bg-primary/70"
                              style={{
                                width: `${results.totalResponses > 0 ? Math.max((count.responses / results.totalResponses) * 100, 4) : 0}%`,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : question.type === "rating" ? (
                    <p className={toClassName("mt-2 text-sm", styles.muted)}>
                      Average score: {question.average !== null && question.average !== undefined ? question.average.toFixed(1) : "Unavailable"}
                    </p>
                  ) : (
                    <ul className={toClassName("mt-2 space-y-1 text-sm", styles.muted)}>
                      {Array.isArray(question.responses) && question.responses.length > 0
                        ? question.responses.map((response) => <li key={response}>“{response}”</li>)
                        : <li>No free-text responses yet.</li>}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          {activeSurvey.questions.map((question) => {
            const value = answerDraft[question.id];

            return (
              <div key={question.id} className="space-y-2">
                <label className={toClassName("block text-sm font-medium", styles.title)}>
                  {question.prompt}
                  {question.required ? <span className="ml-1 text-[var(--color-destructive)]">*</span> : null}
                </label>

                {question.type === "single_choice" && question.options ? (
                  <div className="space-y-2">
                    {question.options.map((option) => (
                      <label key={option.id} className={toClassName("flex items-center gap-2 rounded-lg border px-3 py-2 text-sm", styles.field)}>
                        <input
                          type="radio"
                          name={question.id}
                          checked={value === option.id}
                          onChange={() => updateAnswer(question.id, option.id)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                ) : null}

                {question.type === "multiple_choice" && question.options ? (
                  <div className="space-y-2">
                    {question.options.map((option) => {
                      const selectedValues = Array.isArray(value) ? value : [];
                      const checked = selectedValues.includes(option.id);

                      return (
                        <label key={option.id} className={toClassName("flex items-center gap-2 rounded-lg border px-3 py-2 text-sm", styles.field)}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const next = checked
                                ? selectedValues.filter((entry) => entry !== option.id)
                                : [...selectedValues, option.id];
                              updateAnswer(question.id, next);
                            }}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}

                {question.type === "rating" ? (
                  <div className="flex flex-wrap gap-2">
                    {getRatingRange(activeSurvey.surveyType).map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => updateAnswer(question.id, rating)}
                        className={toClassName(
                          "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                          value === rating ? styles.button : styles.secondaryButton,
                        )}
                      >
                        {rating}
                      </button>
                    ))}
                  </div>
                ) : null}

                {question.type === "open_text" ? (
                  <textarea
                    value={typeof value === "string" ? value : ""}
                    onChange={(event) => updateAnswer(question.id, event.target.value)}
                    className={toClassName("min-h-24 w-full rounded-lg px-3 py-2 text-sm", styles.field)}
                    placeholder="Type your answer"
                  />
                ) : null}
              </div>
            );
          })}

          {error ? <p className="text-sm text-[var(--color-destructive)]">{error}</p> : null}

          <button
            type="button"
            disabled={isSubmitting === activeSurvey.id}
            onClick={() => void handleSubmit()}
            className={toClassName("w-full rounded-lg px-4 py-2 text-sm font-medium transition-colors", styles.button)}
          >
            {isSubmitting === activeSurvey.id ? "Submitting..." : "Submit survey"}
          </button>
        </div>
      )}
    </section>
  );
}
